/**
 * The intake workspace — an enquiry as a sectioned page.
 *
 * Sections: Details (team gates extraction) · Candidates (extraction +
 * INLINE review — the old modal is gone) · Conversation (the thread is
 * the source set: message/file toggles feed extraction). A sticky
 * action strip owns the primary — "Extract report requests" until
 * candidates exist, then "Create N requests" (append-aware) — with
 * "Resolve without converting…" and manual creation in the overflow.
 * The rail carries the child requests (rich summaries), files, and the
 * resolution record.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import {
  ActionIcon,
  Alert,
  Anchor,
  Badge,
  Button,
  Card,
  Center,
  Code,
  Container,
  Grid,
  Group,
  Loader,
  Menu,
  Modal,
  Select,
  Stack,
  Text,
  Textarea,
  Title,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconBan, IconCircleCheck, IconDotsVertical } from '@tabler/icons-react';

import { CreateReportRequestModal } from '@/components';
import { CaseSharing } from '@/components/CaseSharing';
import {
  ConversationFiles,
  EnquiryConversation,
  ExtractionReviewPanel,
  SourceSetSelection,
} from '@/components/Chat';
import { EditableCodename } from '@/components/EditableCodename';
import { EnquiryTeamLookup } from '@/components/EnquiryTeamLookup';
import { FamilyStrip, StageSection, StageSectionState } from '@/components/TriageWorkspace';
import classes from '@/components/TriageWorkspace/TriageWorkspace.module.css';
import {
  useConversationAttachments,
  useEnquiry,
  useExtractReportRequests,
  useReportConfigs,
  useTeams,
} from '@/hooks';
import { useCaseRedirect } from '@/hooks/useCaseRedirect';
import { vnextApiClient } from '@/services/vnext-api';
import {
  EnquiryChildRequest,
  EnquiryConvertResponse,
  EnquiryDetail,
  EnquiryResolution,
  EnquiryTeamResolution,
  formatResolution,
  formatStage,
  RESOLVABLE_CATEGORIES,
  stageColor,
} from '@/types/enquiry';
import { ExtractionSourceSet } from '@/types/extraction';
import { TriageStage, triageStageLabel } from '@/types/report-request';

function emptySelection(): SourceSetSelection {
  return { messageIds: new Set<string>(), attachmentIds: new Set<string>() };
}

type IntakeSectionSlug = 'details' | 'candidates' | 'conversation';

/**
 * The intake workspace as a mountable component — the case page embeds
 * it per selected chain node; the standalone route wraps it after its
 * case redirect fails (orphan fallback).
 */
export function EnquiryIntakeWorkspace({
  enquiryId,
  embedded = false,
  onFamilyChanged,
  onFamilyRenamed,
}: Readonly<{
  enquiryId: string | undefined;
  embedded?: boolean;
  /** Fired after convert/append/resolve — the case page refetches its
   * chain so new requests appear in the rail immediately. */
  onFamilyChanged?: () => void | Promise<void>;
  /** Fired with the new family key after a rename. The family key is the
   * case's URL identity, so the case page must follow it. */
  onFamilyRenamed?: (newFamilyKey: string) => void;
}>) {
  const { detail, isLoading, error, refetch, extractConvert, resolve, setTeamId, resolveTeam } =
    useEnquiry(enquiryId);

  if (isLoading && !detail) {
    return (
      <Center h={400}>
        <Loader />
      </Center>
    );
  }

  if (error || !detail) {
    return <Text c="red">{error?.message ?? 'Enquiry not found'}</Text>;
  }

  return (
    <IntakeWorkspace
      detail={detail}
      embedded={embedded}
      refetch={refetch}
      onFamilyRenamed={onFamilyRenamed}
      extractConvert={async (body, append) => {
        const result = await extractConvert(body, append);
        await onFamilyChanged?.();
        return result;
      }}
      resolve={async (body) => {
        await resolve(body);
        await onFamilyChanged?.();
      }}
      setTeamId={setTeamId}
      resolveTeam={resolveTeam}
    />
  );
}

export function EnquiryDetailPage() {
  const { enquiryId } = useParams<{ enquiryId: string }>();
  const navigate = useNavigate();
  const redirect = useCaseRedirect('enquiry', 'enq', enquiryId);

  if (redirect === 'resolving') {
    return (
      <Center h={400}>
        <Loader />
      </Center>
    );
  }

  return (
    <Container size="xl" py="md">
      <Stack gap="md">
        <Group justify="flex-start">
          <Button variant="subtle" size="xs" onClick={() => navigate('/inbox')}>
            ← Back to inbox
          </Button>
        </Group>
        {/* key= remounts on param-only navigation (React Router reuses the
            page instance), so intake state never leaks between enquiries. */}
        <EnquiryIntakeWorkspace key={enquiryId} enquiryId={enquiryId} />
      </Stack>
    </Container>
  );
}

// ---------------------------------------------------------------------------
// Workspace body
// ---------------------------------------------------------------------------

interface IntakeWorkspaceProps {
  detail: EnquiryDetail;
  /** Case-page mount: the chain rail owns the family strip. */
  embedded?: boolean;
  refetch: () => Promise<void>;
  onFamilyRenamed?: (newFamilyKey: string) => void;
  extractConvert: ReturnType<typeof useEnquiry>['extractConvert'];
  resolve: ReturnType<typeof useEnquiry>['resolve'];
  setTeamId: ReturnType<typeof useEnquiry>['setTeamId'];
  resolveTeam: ReturnType<typeof useEnquiry>['resolveTeam'];
}

function IntakeWorkspace({
  detail,
  embedded = false,
  refetch,
  onFamilyRenamed,
  extractConvert,
  resolve,
  setTeamId,
  resolveTeam,
}: Readonly<IntakeWorkspaceProps>) {
  const navigate = useNavigate();
  const [threadId, setThreadId] = useState<string | undefined>();
  // Shared source-set selection — the Conversation section (message/file
  // toggles) and the Files rail card both write here.
  const [selection, setSelection] = useState<SourceSetSelection>(emptySelection);
  const extract = useExtractReportRequests(detail.id);
  const { attachments } = useConversationAttachments(threadId);
  const { configs, isLoading: configsLoading } = useReportConfigs({
    teamId: detail.teamId ?? undefined,
    isActive: true,
  });
  const [manualCreateOpen, setManualCreateOpen] = useState(false);
  const [resolveOpen, { open: openResolve, close: closeResolve }] = useDisclosure(false);
  const [submitting, setSubmitting] = useState(false);

  const resolved = detail.stage === 'resolved';
  const triaged = detail.stage === 'triaged';

  // Message and attachment selection are independent: ticking "Use for
  // extraction" on a message selects ONLY the message body (long documents
  // blew the prompt; files opt in via their own checkboxes).
  const toggleMessage = useCallback((id: string) => {
    setSelection((prev) => {
      const messageIds = new Set(prev.messageIds);
      if (messageIds.has(id)) {
        messageIds.delete(id);
      } else {
        messageIds.add(id);
      }
      return { ...prev, messageIds };
    });
  }, []);

  const toggleAttachment = useCallback((id: string) => {
    setSelection((prev) => {
      const next = new Set(prev.attachmentIds);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return { ...prev, attachmentIds: next };
    });
  }, []);

  const selectedCount = selection.messageIds.size + selection.attachmentIds.size;

  const runExtraction = useCallback(async () => {
    const sourceSet: ExtractionSourceSet = {
      message_ids: [...selection.messageIds],
      attachment_ids: [...selection.attachmentIds],
      // Fold the enquiry body in when nothing else is explicitly picked.
      include_enquiry_body: selectedCount === 0,
    };
    await extract.extract(sourceSet);
  }, [selection, selectedCount, extract]);

  // The inline review panel registers its submit + chosen-count so the
  // strip's primary can drive it (same pattern as the triage Define
  // section).
  const reviewSubmitRef = useRef<(() => Promise<EnquiryConvertResponse | undefined>) | null>(null);
  const [chosenCount, setChosenCount] = useState(0);
  const registerReviewSubmit = useCallback(
    (submit: (() => Promise<EnquiryConvertResponse | undefined>) | null, count: number) => {
      reviewSubmitRef.current = submit;
      setChosenCount(count);
    },
    []
  );

  const hasCandidates = extract.candidates.length > 0;

  const handleCreate = useCallback(async () => {
    setSubmitting(true);
    try {
      const result = await reviewSubmitRef.current?.();
      extract.reset();
      setSelection(emptySelection());
      // Land the analyst on the triaged work, not the inbound page. The
      // triage route resolves the codename family and re-points to the
      // case view with the request selected.
      const firstRequestId = result?.report_request_ids[0];
      if (firstRequestId) {
        navigate(`/admin/triage/${firstRequestId}`);
      }
    } finally {
      setSubmitting(false);
    }
  }, [extract, navigate]);

  // --- sections -------------------------------------------------------------
  const [expanded, setExpanded] = useState<Record<IntakeSectionSlug, boolean>>({
    details: detail.teamId === null,
    candidates: true,
    conversation: true,
  });
  const toggle = (slug: IntakeSectionSlug, open: boolean) =>
    setExpanded((prev) => ({ ...prev, [slug]: open }));

  const detailsState: StageSectionState = resolved
    ? 'record'
    : detail.teamId
      ? 'completed'
      : 'active';
  const candidatesState: StageSectionState = resolved
    ? 'record'
    : detail.teamId
      ? 'active'
      : 'locked';

  const configOptions = configs.map((c) => ({ value: c.id, label: c.name }));

  // Assigning a team can re-mint a random codename into the client's scheme,
  // which changes the family key the case URL is addressed by. Follow it, or
  // the case page keeps polling the old key and 404s.
  const handleSetTeamId = async (teamId: string | null) => {
    const updated = await setTeamId(teamId);
    if (updated?.codename && updated.codename !== detail.codename) {
      onFamilyRenamed?.(updated.codename);
    }
  };

  // An exact match assigns the team, which can rename the case the same way.
  const handleResolveTeam = async () => {
    const result = await resolveTeam();
    const renamed = result?.enquiry.codename;
    if (renamed && renamed !== detail.codename) {
      onFamilyRenamed?.(renamed);
    }
    return result;
  };

  return (
    <Stack gap="md">
      <IntakeHeader
        detail={detail}
        onCodenameRenamed={async (newFamilyKey) => {
          await refetch();
          onFamilyRenamed?.(newFamilyKey);
        }}
        onStaleKey={refetch}
      />

      {!embedded && <FamilyStrip current={{ type: 'enquiry', id: detail.id }} enquiry={detail} />}

      {/* Embedded in the case page, which carries the control itself. An
          enquiry with no team has nobody to share with yet. */}
      {!embedded && detail.teamId !== null && (
        <CaseSharing anchor={{ kind: 'enquiry', id: detail.id }} />
      )}

      {/* Sticky action strip — the workspace's single primary. */}
      <Card
        shadow="sm"
        padding="xs"
        radius="md"
        className={classes.gateStrip}
        data-testid="intake-strip"
      >
        <Group justify="space-between" wrap="nowrap" gap="sm">
          <Group gap={6} wrap="wrap">
            <Badge
              size="sm"
              radius="sm"
              variant="light"
              color={stageColor(detail.stage)}
              data-testid="intake-stage"
            >
              {formatStage(detail.stage)}
            </Badge>
            {!resolved && detail.teamId === null && (
              <Badge
                size="sm"
                radius="xl"
                variant="light"
                color="orange"
                component="button"
                type="button"
                style={{ cursor: 'pointer', border: 'none' }}
                onClick={() => toggle('details', true)}
                data-testid="intake-blocker-team"
              >
                Assign a team to extract
              </Badge>
            )}
            {triaged && (
              <Text size="xs" c="dimmed">
                {detail.reportRequests.length} request
                {detail.reportRequests.length === 1 ? '' : 's'} created — extracting again appends.
              </Text>
            )}
          </Group>
          <Group gap={6} wrap="nowrap">
            {resolved ? (
              <Badge color="dark" variant="filled" leftSection={<IconCircleCheck size={12} />}>
                Resolved — {formatResolution(detail.resolutionCategory)}
              </Badge>
            ) : hasCandidates ? (
              <Button
                size="xs"
                onClick={() => void handleCreate()}
                disabled={chosenCount === 0}
                loading={submitting}
                data-testid="intake-primary"
              >
                {triaged ? 'Add' : 'Create'} {chosenCount || ''} request
                {chosenCount === 1 ? '' : 's'}
              </Button>
            ) : (
              <Button
                size="xs"
                onClick={() => void runExtraction()}
                disabled={detail.teamId === null}
                loading={extract.isExtracting}
                data-testid="intake-primary"
              >
                Extract report requests
              </Button>
            )}
            {detail.stage === 'received' && (
              <Menu position="bottom-end" withinPortal>
                <Menu.Target>
                  <ActionIcon variant="subtle" color="gray" aria-label="More actions">
                    <IconDotsVertical size={16} />
                  </ActionIcon>
                </Menu.Target>
                <Menu.Dropdown>
                  {detail.stage === 'received' && (
                    <Menu.Item
                      color="red"
                      leftSection={<IconBan size={14} />}
                      onClick={openResolve}
                      data-testid="intake-resolve"
                    >
                      Resolve without converting…
                    </Menu.Item>
                  )}
                </Menu.Dropdown>
              </Menu>
            )}
          </Group>
        </Group>
      </Card>

      {resolved && (
        <Alert color="dark" data-testid="resolved-banner">
          <Stack gap={4}>
            <Text size="sm" fw={600}>
              Resolved as {formatResolution(detail.resolutionCategory)} — no requests will be
              created.
            </Text>
            {detail.resolutionNotes && <Text size="sm">{detail.resolutionNotes}</Text>}
            {detail.mergedIntoId && (
              <Text size="sm">
                Merged into{' '}
                <Anchor component={Link} to={`/admin/enquiries/${detail.mergedIntoId}`} size="sm">
                  <Code>{detail.mergedIntoId.slice(0, 8)}…</Code>
                </Anchor>
              </Text>
            )}
          </Stack>
        </Alert>
      )}

      <Grid gutter="md">
        <Grid.Col span={{ base: 12, md: 8 }}>
          <Stack gap="md">
            <StageSection
              slug="details"
              index={1}
              label="Details"
              state={detailsState}
              expanded={expanded.details}
              onToggle={(open) => toggle('details', open)}
              summary={<DetailsSummary detail={detail} />}
            >
              <DetailsBody
                detail={detail}
                onSetTeamId={handleSetTeamId}
                onResolveTeam={handleResolveTeam}
              />
            </StageSection>

            <StageSection
              slug="candidates"
              index={2}
              label="Candidates"
              state={candidatesState}
              expanded={expanded.candidates && candidatesState !== 'locked'}
              onToggle={(open) => toggle('candidates', open)}
              lockedHint="Assign a team first — extraction maps candidates to the team's report configs."
              summary={
                triaged ? (
                  <Text size="sm" c="dimmed">
                    {detail.reportRequests.length} request
                    {detail.reportRequests.length === 1 ? '' : 's'} created
                  </Text>
                ) : undefined
              }
            >
              <Stack gap="sm">
                {!hasCandidates && (
                  <Text size="sm" c="dimmed">
                    {selectedCount > 0
                      ? `${selectedCount} source(s) selected below — extraction reads only those.`
                      : 'Select messages/files in the conversation to narrow the sources, or extract from the whole enquiry.'}
                  </Text>
                )}
                {extract.error && (
                  <Text size="sm" c="red">
                    {extract.error instanceof Error ? extract.error.message : String(extract.error)}
                  </Text>
                )}
                {hasCandidates && (
                  <>
                    <Group justify="space-between">
                      <Text size="sm" c="dimmed">
                        Pick a report config per candidate — candidates without one are skipped.
                      </Text>
                      <Button
                        variant="subtle"
                        size="compact-xs"
                        onClick={() => extract.reset()}
                        data-testid="clear-extraction"
                      >
                        Clear & re-extract
                      </Button>
                    </Group>
                    <ExtractionReviewPanel
                      candidates={extract.candidates}
                      configOptions={configOptions}
                      configsLoading={configsLoading}
                      files={attachments}
                      teamId={detail.teamId}
                      alreadyTriaged={triaged}
                      isSubmitting={submitting}
                      hideActions
                      registerSubmit={registerReviewSubmit}
                      onConfirm={extractConvert}
                    />
                  </>
                )}
              </Stack>
            </StageSection>

            <StageSection
              slug="conversation"
              index={3}
              label="Conversation"
              state="record"
              expanded={expanded.conversation}
              onToggle={(open) => toggle('conversation', open)}
            >
              <div style={{ display: 'flex', flexDirection: 'column', minHeight: '50vh' }}>
                <EnquiryConversation
                  entityId={detail.id}
                  selection={selection}
                  onToggleMessage={toggleMessage}
                  onToggleAttachment={toggleAttachment}
                  onThreadResolved={setThreadId}
                />
              </div>
            </StageSection>
          </Stack>
        </Grid.Col>

        <Grid.Col span={{ base: 12, md: 4 }}>
          <Stack gap="md">
            <RequestsCard
              detail={detail}
              onCreateManually={resolved ? undefined : () => setManualCreateOpen(true)}
            />
            <ConversationFiles
              threadId={threadId}
              selection={selection}
              onToggleAttachment={toggleAttachment}
              reportRequestIds={detail.reportRequestIds}
              onAttachToRr={async (attachmentId, rrId) => {
                await vnextApiClient.attachConversationFileToReportRequest(rrId, attachmentId);
              }}
            />
          </Stack>
        </Grid.Col>
      </Grid>

      <ResolveModal
        opened={resolveOpen}
        onClose={closeResolve}
        onResolve={async (body) => {
          await resolve(body);
          closeResolve();
        }}
      />

      <CreateReportRequestModal
        opened={manualCreateOpen}
        onClose={() => setManualCreateOpen(false)}
        configOptions={configOptions}
        configsLoading={configsLoading}
        files={attachments}
        teamId={detail.teamId}
        alreadyTriaged={triaged}
        isSubmitting={submitting}
        onCreate={async (body, append) => {
          setSubmitting(true);
          try {
            await extractConvert(body, append);
          } finally {
            setSubmitting(false);
          }
        }}
      />
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Header
// ---------------------------------------------------------------------------

function IntakeHeader({
  detail,
  onCodenameRenamed,
  onStaleKey,
}: Readonly<{
  detail: EnquiryDetail;
  onCodenameRenamed?: (newFamilyKey: string) => void | Promise<void>;
  onStaleKey?: () => void | Promise<void>;
}>) {
  return (
    <Group justify="space-between" align="flex-start" wrap="wrap">
      <Stack gap={2}>
        {detail.codename ? (
          <EditableCodename
            value={detail.codename}
            size="md"
            fw={700}
            onSaved={onCodenameRenamed}
            onStaleKey={onStaleKey}
            data-testid="enquiry-codename"
          />
        ) : (
          <Title order={3}>Enquiry {detail.id.slice(0, 8)}…</Title>
        )}
        <Group gap={6}>
          <Text size="sm" c="dimmed">
            {detail.rawPayload.subject ?? '(no subject)'}
          </Text>
          {detail.rawPayload.requester_email && (
            <>
              <Text size="sm" c="dimmed">
                ·
              </Text>
              <Text size="sm" c="dimmed">
                {detail.rawPayload.requester_email}
              </Text>
            </>
          )}
          <Text size="sm" c="dimmed">
            · {detail.source} · {detail.sourceExternalId ?? 'no external id'}
          </Text>
        </Group>
      </Stack>
    </Group>
  );
}

// ---------------------------------------------------------------------------
// Details section
// ---------------------------------------------------------------------------

function DetailsSummary({ detail }: Readonly<{ detail: EnquiryDetail }>) {
  const { teams } = useTeams();
  const teamName =
    teams.find((t) => String(t.id) === String(detail.teamId ?? ''))?.name ?? detail.teamId;
  return (
    <Text size="sm">
      {teamName ?? 'No team'}
      {detail.clientCode ? ` · ${detail.clientCode}` : ''}
      {detail.rawPayload.requester_email ? ` · ${detail.rawPayload.requester_email}` : ''}
    </Text>
  );
}

function DetailsBody({
  detail,
  onSetTeamId,
  onResolveTeam,
}: Readonly<{
  detail: EnquiryDetail;
  onSetTeamId: (teamId: string | null) => Promise<void>;
  onResolveTeam: () => Promise<EnquiryTeamResolution | undefined>;
}>) {
  const { teams } = useTeams();
  const [pending, setPending] = useState<string | null>(detail.teamId ?? null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setPending(detail.teamId ?? null);
  }, [detail.teamId]);

  const teamOptions = teams.map((t) => ({ value: String(t.id), label: t.name }));
  const dirty = pending !== (detail.teamId ?? null);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSetTeamId(pending);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Stack gap="sm">
      <Text size="xs" c="dimmed">
        The client team this enquiry belongs to — auto-resolved from the requester's email when
        possible. Extraction needs it to map candidates onto the team's report configs.
      </Text>
      <EnquiryTeamLookup
        requesterEmail={detail.rawPayload.requester_email ?? null}
        teamId={detail.teamId}
        onLookup={onResolveTeam}
        onUseSuggestion={onSetTeamId}
        teamName={(id) => teams.find((t) => String(t.id) === id)?.name ?? id}
      />
      <Group gap="sm" align="flex-end" wrap="nowrap">
        <Select
          label="Team"
          placeholder="Select a team"
          data={teamOptions}
          value={pending}
          onChange={setPending}
          searchable
          clearable
          w={300}
        />
        <Button onClick={handleSave} loading={saving} disabled={!dirty} data-testid="team-save">
          Save
        </Button>
      </Group>
      {detail.clientCode && (
        <Text size="sm" data-testid="client-code">
          Client code: <b>{detail.clientCode}</b>
        </Text>
      )}
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Requests rail card — rich child summaries
// ---------------------------------------------------------------------------

function RequestsCard({
  detail,
  onCreateManually,
}: Readonly<{ detail: EnquiryDetail; onCreateManually?: () => void }>) {
  if (detail.stage === 'resolved' && detail.reportRequests.length === 0) {
    return null;
  }

  return (
    <Card shadow="sm" padding="md" radius="md">
      <Stack gap="xs">
        <Group justify="space-between" align="center">
          <Text fw={600} size="sm">
            Requests ({detail.reportRequests.length})
          </Text>
          {onCreateManually && (
            <Button
              variant="light"
              size="compact-xs"
              onClick={onCreateManually}
              disabled={detail.teamId === null}
              title={detail.teamId === null ? 'Assign a team first' : undefined}
              data-testid="intake-create-manually"
            >
              Create manually
            </Button>
          )}
        </Group>
        {detail.reportRequests.length === 0 && (
          <Text c="dimmed" size="sm">
            Extract report requests from the conversation, or create one manually.
          </Text>
        )}
        {detail.reportRequests.map((rr) => (
          <ChildRequestRow key={rr.id} rr={rr} />
        ))}
      </Stack>
    </Card>
  );
}

function ChildRequestRow({ rr }: Readonly<{ rr: EnquiryChildRequest }>) {
  const started = rr.triageStage === 'started';
  return (
    <Stack gap={2} data-testid={`child-request-${rr.id}`}>
      <Group gap="xs" wrap="nowrap" justify="space-between">
        <Anchor component={Link} to={`/admin/triage/${rr.id}`} size="sm" fw={500}>
          {rr.codename ?? `${rr.id.slice(0, 8)}…`}
        </Anchor>
        <Badge size="xs" variant="light" color={started ? 'green' : 'blue'}>
          {triageStageLabel(rr.triageStage as TriageStage)}
        </Badge>
      </Group>
      <Group gap={6}>
        {rr.configName &&
          (rr.reportConfigId ? (
            <Anchor
              component={Link}
              to={`/admin/report-configs/${rr.reportConfigId}`}
              size="xs"
              c="dimmed"
              underline="hover"
            >
              {rr.configName}
            </Anchor>
          ) : (
            <Text size="xs" c="dimmed">
              {rr.configName}
            </Text>
          ))}
        {(rr.subjectStrings ?? []).length > 0 && (
          <Text size="xs" c="dimmed" truncate="end">
            · {(rr.subjectStrings ?? []).join(', ')}
          </Text>
        )}
        {rr.expedited && (
          <Badge size="xs" color="orange" variant="light">
            ⚡
          </Badge>
        )}
        {started && rr.reportId && (
          <Anchor component={Link} to={`/reports/${rr.reportId}`} size="xs">
            View report →
          </Anchor>
        )}
      </Group>
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Resolve modal (secondary — "Resolve without converting…")
// ---------------------------------------------------------------------------

function ResolveModal({
  opened,
  onClose,
  onResolve,
}: Readonly<{
  opened: boolean;
  onClose: () => void;
  onResolve: (body: {
    category: EnquiryResolution;
    notes?: string | null;
    merged_into_id?: string | null;
  }) => Promise<void>;
}>) {
  const [category, setCategory] = useState<EnquiryResolution | null>(null);
  const [notes, setNotes] = useState('');
  const [mergedIntoId, setMergedIntoId] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!category) {
      return;
    }
    setSubmitting(true);
    try {
      await onResolve({
        category,
        notes: notes || null,
        merged_into_id: category === 'merged' ? mergedIntoId || null : null,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal opened={opened} onClose={onClose} title="Resolve without converting">
      <Stack gap="sm">
        <Text c="dimmed" size="sm">
          Use when the enquiry is a quote-only ask, out of scope, spam, or a duplicate of another
          enquiry. No report requests will be created.
        </Text>
        <Select
          label="Category"
          placeholder="Pick a resolution"
          data={RESOLVABLE_CATEGORIES.map((c) => ({ value: c.value, label: c.label }))}
          value={category}
          onChange={(v) => setCategory(v as EnquiryResolution | null)}
        />
        {category === 'merged' && (
          <Textarea
            label="Merged into (Enquiry id)"
            value={mergedIntoId}
            onChange={(e) => setMergedIntoId(e.currentTarget.value)}
            placeholder="UUID of the surviving enquiry"
            autosize
            minRows={1}
          />
        )}
        <Textarea
          label="Notes"
          value={notes}
          onChange={(e) => setNotes(e.currentTarget.value)}
          autosize
          minRows={2}
        />
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={submit} loading={submitting} disabled={!category} color="red">
            Resolve
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
